<template>
    <Modal
        id="modal-checkin"
        size="xl"
        :title="title"
        :okTitle="$t('modal.save')"
        :okDisabled="isSubmitting || canNotCreateOrEditCheckIn"
        :cancelTitle="$t('modal.cancel')"
        :cancelDisabled="isSubmitting"
        :noCloseOnEsc="true"
        :noCloseOnBackdrop="true"
        @okHandler="onSubmit"
        @hideHandler="onCloseModal"
        isModalRoute
    >
        <Loader :loader="loader" />
        <div v-if="canNotCreateOrEditCheckIn">
            <b-alert v-if="!loader" show variant="warning" class="mt-3">
                <i class="fa fa-warning"></i>
                <span v-if="!isEdit">{{ $t('notification.permissions.createCheckInDenied') }}</span>
                <span v-else>{{ $t('notification.permissions.updateCheckInDenied') }}</span>
            </b-alert>
        </div>
        <div v-else>
            <div v-if="!loader">
                <b-container fluid>
                    <b-row>
                        <b-col>
                            <p class="h4 mb-0">
                                {{ current.title }}
                            </p>
                        </b-col>
                        <div class="w-100" />
                        <b-col>
                            <hr />
                        </b-col>
                        <div class="w-100" />
                        <b-col sm="12" lg="6">
                            <p>
                                <strong>{{ $t('checkIn.lastCheckInValue') }}&nbsp;</strong>
                                <span>{{ last.checkInValue }}</span>
                            </p>
                        </b-col>
                        <b-col sm="12" lg="6">
                            <p>
                                <strong>{{ $t('checkIn.targetValue') }}&nbsp;</strong>
                                <span>{{ current.targetValue }}</span>
                            </p>
                        </b-col>
                    </b-row>
                </b-container>
                <b-form id="form-checkin" novalidate>
                    <b-container fluid>
                        <b-row>
                            <b-col sm="12" lg="6">
                                <c-check-in-by-range
                                    v-if="$v.form.isCheckInByRange.$model"
                                    id="current-value"
                                    :label="`${$t('checkIn.currentValue')}*`"
                                    :value.sync="$v.form.currentValue.$model"
                                    :selected.sync="$v.form.rangeId.$model"
                                    :state="checkState('currentValue')"
                                    labelClass="font-weight-bold"
                                    :goalRanges="goalRanges"
                                >
                                    <span v-if="!$v.form.currentValue.required">
                                        {{ $t('form.validations.required') }}
                                    </span>
                                    <span v-if="!$v.form.currentValue.decimal">
                                        {{ $t('form.validations.decimal') }}
                                    </span>
                                </c-check-in-by-range>
                                <c-input
                                    v-else
                                    id="current-value"
                                    :label="`${$t('checkIn.currentValue')}*`"
                                    :placeholder="$t('form.placeholder.currentValue')"
                                    :value.sync="$v.form.currentValue.$model"
                                    :state="checkState('currentValue')"
                                    labelClass="font-weight-bold"
                                >
                                    <span v-if="!$v.form.currentValue.required">
                                        {{ $t('form.validations.required') }}
                                    </span>
                                    <span v-if="!$v.form.currentValue.decimal">
                                        {{ $t('form.validations.decimal') }}
                                    </span>
                                </c-input>
                            </b-col>
                            <b-col sm="12" lg="6">
                                <c-satisfaction
                                    :label="$t('checkIn.howAmIFeeling')"
                                    :value.sync="$v.form.satisfaction.$model"
                                />
                            </b-col>
                            <div class="w-100" />
                            <b-col sm="12" lg="6">
                                <c-text-area
                                    id="general-comments"
                                    :label="$t('checkIn.generalComments')"
                                    :value.sync="$v.form.generalComments.$model"
                                    labelClass="font-weight-bold"
                                />
                            </b-col>
                            <b-col sm="12" lg="6">
                                <c-text-area
                                    id="difficulties-found"
                                    :label="$t('checkIn.difficultiesFound')"
                                    :value.sync="$v.form.difficultiesFound.$model"
                                    labelClass="font-weight-bold"
                                />
                            </b-col>
                            <b-col sm="12" lg="6">
                                <b-form-group :label="$t('checkIn.evidences')" label-class="font-weight-bold">
                                    <b-form-file
                                        id="evidence"
                                        v-model="evidence"
                                        :placeholder="evidencePlaceholder"
                                        :accept="acceptedExtensions"
                                        :browse-text="$t('form.button.search')"
                                    ></b-form-file>
                                    <div>
                                        <small class="text-danger" v-show="evidence && evidenceTooLarge">
                                            {{ $t('checkIn.evidenceTooLarge') }}
                                        </small>
                                    </div>
                                    <div>
                                        <small class="text-danger" v-show="evidence && invalidEvidenceExtension">
                                            {{ $t('form.validations.invalidExtension') }}
                                        </small>
                                    </div>
                                </b-form-group>
                            </b-col>
                            <b-col sm="12" lg="6">
                                <div v-if="hasEvidence">
                                    <b-form-group
                                        :label="$t('checkIn.downloadEvidences')"
                                        label-class="font-weight-bold"
                                    >
                                        <i
                                            class="fas fa-download fa-2x"
                                            @click="downloadEvidence(checkinId, evidencePlaceholder)"
                                        ></i>
                                    </b-form-group>
                                </div>
                            </b-col>
                        </b-row>
                    </b-container>
                </b-form>
            </div>
        </div>
        <template slot="okButton">
            <i v-if="isSubmitting" class="fa fa-spinner fa-spin px-2" />
        </template>
    </Modal>
</template>

<script>
import i18n from '@/i18n'
import Swal from 'sweetalert2'
import Loader from '@/views/components/loader'
import { getParam, getQuery } from '@/lib/url'
import { getUserPermissions } from '@/services/user-permissions'
import Modal from '@/views/components/modal'
import { CTextArea } from '@/views/components/fields'
import notification, { success, danger } from '@/lib/notification'
import { validationMixin } from 'vuelidate'
import validationOfFields from './validation'
import { getLastCheckInFromApi, setCurrentCheckin, getCheckInFromApi, updateCheckIn } from '../api'
import initialValuesOfFields from './initial-values'
import mapperFormToApi from './mapperFormToApi'
import mapperApiToForm from './mapperApiToForm'
import { CSatisfaction, CInput, CCheckInByRange } from '../components/fields'
import api from '@/services/api'
import { userPermissionsMixin } from '@/mixins'

notification.init()

const NA = 'N/A'
const allowedExtensions = [
    'doc',
    'docx',
    'xls',
    'xlsx',
    'ppt',
    'pptx',
    'pdf',
    'png',
    'jpg',
    'ods',
    'odt',
    'odp',
    'gif',
    'txt',
    'pst',
    'ost',
]

export default {
    name: 'CheckIn',
    components: {
        Modal,
        CSatisfaction,
        CTextArea,
        CInput,
        CCheckInByRange,
        Loader,
    },
    mixins: [validationMixin, userPermissionsMixin],
    validations: {
        form: { ...validationOfFields },
    },
    props: {
        title: {
            type: String,
            required: false,
            default: i18n.t('checkIn.checkIn'),
        },
    },
    data() {
        return {
            last: {
                checkInValue: '',
            },
            current: {
                title: '',
                level: '',
                targetValue: '',
                evidence: '',
            },
            isSubmitting: false,
            form: { ...initialValuesOfFields },
            indicatorId: null,
            indicatorType: null,
            checkinId: getParam('checkinId', this),
            expectationId: getParam('expectationId', this),
            checkinType: getQuery('type', this),
            goalRanges: null,
            loader: true,
            permissions: getUserPermissions(),
            evidence: null,
            evidencePlaceholder: this.$t('checkIn.chooseFile'),
            evidenceTooLarge: false,
            invalidEvidenceExtension: false,
            hasEvidence: false,
        }
    },
    mounted() {
        if (this.$route.params.targetId) {
            this.indicatorId = this.$route.params.targetId
            this.indicatorType = 'target'
        } else if (this.$route.params.kpiId) {
            this.indicatorId = this.$route.params.kpiId
            this.indicatorType = 'kpi'
        }

        if (!this.userHasRolePermission) {
            const stagePermissions = this.$store.state.currentUser.user.permissions
            this.permissions.current_event_permissions = stagePermissions
        }

        if (this.isEdit) {
            getCheckInFromApi(this.indicatorId, this.checkinId, this.indicatorType)
                .then((resp) => {
                    const { indicator_title, indicator_level, target, indicator_ranges, last_checkin_value } =
                        resp.data.data
                    this.last.checkInValue = last_checkin_value || NA
                    this.goalRanges = indicator_ranges
                    this.current = {
                        title: indicator_title,
                        level: indicator_level,
                        targetValue: target || NA,
                    }
                    this.form = mapperApiToForm(resp.data.data)
                    if (resp.data.data.evidence) {
                        this.evidencePlaceholder = resp.data.data.evidence.split('/').pop()
                        this.hasEvidence = true
                    }
                })
                .finally(() => {
                    this.loader = false
                })
        } else if (this.isRetroactive) {
            getCheckInFromApi(this.indicatorId, this.expectationId, this.indicatorType)
                .then((resp) => {
                    const {
                        indicator_title,
                        indicator_level,
                        target,
                        indicator_ranges,
                        last_checkin_value,
                        isCheckInByRange,
                        date,
                    } = resp.data.data
                    this.last.checkInValue = last_checkin_value || NA
                    this.goalRanges = indicator_ranges
                    this.current = {
                        title: indicator_title,
                        level: indicator_level,
                        targetValue: target || NA,
                    }
                    this.form = {
                        ...initialValuesOfFields,
                        isCheckInByRange,
                        date,
                    }
                })
                .finally(() => {
                    this.loader = false
                })
        } else {
            getLastCheckInFromApi(this.indicatorId, this.indicatorType)
                .then((resp) => {
                    const {
                        indicator_title,
                        indicator_level,
                        target,
                        indicator_ranges,
                        lastCheckInValue,
                        isCheckInByRange,
                    } = resp.data.data
                    this.last.checkInValue = lastCheckInValue || NA
                    this.current = {
                        title: indicator_title,
                        level: indicator_level,
                        targetValue: target || NA,
                    }
                    this.goalRanges = indicator_ranges
                    this.form = { ...initialValuesOfFields, isCheckInByRange }
                })
                .finally(() => {
                    this.loader = false
                })
        }
    },
    methods: {
        onSubmit(event, closeModal) {
            event.preventDefault()
            if (this.validate()) {
                this.isSubmitting = true
                this.$nextTick(() => {
                    if (!this.isEdit) {
                        setCurrentCheckin(this.indicatorId, mapperFormToApi(this.form), this.indicatorType)
                            .then((resp) => {
                                const {
                                    data: { data },
                                } = resp

                                if (resp.data.messages[0].status === 'success') {
                                    this.$emit('update-parent', 'updateCheckin', data)
                                    this.notificationCheckInSuccessfully()
                                    closeModal()
                                } else {
                                    if (resp.data.messages[0].params === 'isCalculated') {
                                        Swal({
                                            type: 'error',
                                            title: i18n.t('notification.somethingWentWrong'),
                                            text: i18n.t('notification.checkin.calculatedAutomatically'),
                                        })
                                    }
                                }
                            })
                            .catch(() => {
                                this.notificationError()
                            })
                            .finally(() => {
                                this.isSubmitting = false
                            })
                    } else {
                        updateCheckIn(this.indicatorId, this.checkinId, mapperFormToApi(this.form), this.indicatorType)
                            .then((resp) => {
                                const {
                                    data: { data },
                                } = resp

                                this.$emit('update-parent', 'updateCheckin', data)
                                this.notificationCheckInSuccessfully()
                                closeModal()
                            })
                            .catch(() => {
                                this.notificationError()
                            })
                            .finally(() => {
                                this.isSubmitting = false
                            })
                    }
                })
            }
        },
        validate() {
            this.$v.$touch()
            const invalidEvidence = this.evidence && (this.evidenceTooLarge || this.invalidEvidenceExtension)
            return !invalidEvidence && !this.$v.$invalid
        },
        checkState(val) {
            const field = this.$v.form[val]
            return field.$dirty ? !field.$invalid : null
        },
        onCloseModal(event, closeModal) {
            const { trigger } = event
            if (trigger == 'header-close' || trigger == 'headerclose' || trigger == 'cancel') {
                if (this.$v.$anyDirty) {
                    event.preventDefault()
                    this.beforeModalCloseAlert(closeModal)
                }
            }
        },
        beforeModalCloseAlert(closeModal) {
            Swal.fire({
                title: i18n.t('notification.areYouSure'),
                text: i18n.t('notification.lostChanges'),
                type: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: i18n.t('notification.yesCloseIt'),
            }).then((result) => {
                if (result.value) {
                    closeModal()
                }
            })
        },
        notificationCheckInSuccessfully() {
            success(
                i18n.t('notification.updatedSuccessfully', {
                    item: i18n.t('checkIn.checkIn'),
                })
            )
        },
        notificationError() {
            danger()
        },
        forceFileDownload(response, filename) {
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')

            link.href = url
            link.setAttribute('download', filename)
            document.body.appendChild(link)
            link.click()
        },
        downloadEvidence(id, filename) {
            try {
                api.get(`/goals/checkins/download/${id}`, {
                    responseType: 'arraybuffer',
                }).then((response) => {
                    this.forceFileDownload(response, filename)
                })
            } catch (e) {
                this.hasEvidence = false
                console.log(e)
            }
        },
    },
    computed: {
        userHasRolePermission() {
            const availableRoles = ['god', 'administrator', 'local_administrator', 'is_target_master']
            const rolesPermission = this.userHasPermission(this.$store.state.currentUser.user, availableRoles, [])
            return rolesPermission
        },
        isEdit() {
            return this.checkinId
        },
        isRetroactive() {
            return this.expectationId
        },
        canEditCheckIn() {
            return this.permissions && this.current.level
                ? this.permissions.current_event_permissions.goals[this.current.level].edit_checkin
                : false
        },
        canDoCheckIn() {
            const isTargetMaster = getUserPermissions().is_target_master

            if (isTargetMaster) {
                return true
            }

            const permissionKey = this.checkinType == 'focal-points' ? 'checkin_as_focal_point' : 'checkin'
            return this.permissions && this.current.level
                ? this.permissions.current_event_permissions.goals[this.current.level][permissionKey]
                : false
        },
        canNotCreateOrEditCheckIn() {
            if (this.isEdit) {
                return !this.canEditCheckIn
            }
            return !this.canDoCheckIn
        },
        acceptedExtensions() {
            return allowedExtensions.map((ext) => '.' + ext).join(', ')
        },
    },
    destroyed() {
        this.$emit('modalClosed')
    },
    watch: {
        evidence: function () {
            const MAX_FILE_SIZE_ALLOWED = 20000000
            this.evidenceTooLarge = this.evidence.size > MAX_FILE_SIZE_ALLOWED
            const fileExtension = this.evidence.name.split('.').pop()
            this.invalidEvidenceExtension = !allowedExtensions.includes(fileExtension.toLowerCase())
            let reader = new FileReader()
            reader.readAsDataURL(this.evidence)
            reader.onload = (event) => {
                this.form.evidence = {
                    blob: event.target.result,
                    filename: this.evidence.name,
                }
            }
        },
    },
}
</script>

<style>
.swal2-container {
    z-index: 9999 !important;
}
</style>
